<template>
  <modal :name="name" :height="height" :width="width" :clickToClose="clickToClose" @opened="$emit('opened')">
    <div class="shared-filter-modal">
      <div class="shared-filter-modal-header">
        <h3 class="shared-filter-modal-title">{{ title }}</h3>
        <span v-if="showSelectedCount" class="shared-filter-modal-counter">
          {{ $t('shared_selected_count', { count: selectedCount }) }}
        </span>
        <slot name="header-extra"></slot>
      </div>

      <div class="shared-filter-modal-filter" v-if="showFilter">
        <h4 v-if="filterLabel || filterInfoTitle" class="shared-filter-modal-filter-label">
          <div class="panel-info" v-if="filterInfoTitle" :title="filterInfoTitle" :alt="filterInfoTitle">
            <i class="info-icon fas fa-info-circle"></i> -
          </div>
          {{ filterLabel }}
        </h4>

        <div class="shared-filter-modal-filter-inputs">
          <input class="shared-filter-modal-input" type="text" :value="filterValue" :placeholder="filterPlaceholder"
            @input="$emit('update:filterValue', $event.target.value)" @keyup.enter="$emit('search')" />

          <button class="button shared-modal-button" @click="$emit('search')"
            :class="{ 'disabled': filterButtonDisabled }" :disabled="filterButtonDisabled">
            {{ filterButtonLabel }}
          </button>
        </div>
      </div>

      <div class="shared-filter-modal-content" :class="{ 'shared-filter-modal-content--framed': !showDefaultList }">
        <template v-if="showDefaultList">
          <div class="shared-list">
            <div class="shared-list-loading" v-if="listLoading">
              <div class="shared-list-spinner"></div>
              {{ listLoadingLabel }}
            </div>

            <label v-else v-for="item in items" :key="item[itemKeyField]" class="shared-list-item">
              <input type="checkbox" class="shared-list-checkbox" :checked="Boolean(item[itemCheckedField])"
                @change="$emit('toggle-item', item)" />
              <span class="shared-list-item-label">{{ getItemLabel(item) }}</span>
            </label>

            <div class="shared-list-empty" v-if="!listLoading && !items.length">
              {{ listEmptyLabel }}
            </div>
          </div>
        </template>

        <grid-component
          v-else-if="grid"
          class="shared-filter-modal-grid"
          :id="gridId"
          :grid="grid"
          :class="gridClass"
        ></grid-component>

        <slot v-else></slot>
      </div>

      <div class="button-div shared-filter-modal-actions">
        <button class="button shared-modal-button" :class="[{ 'outline': true }, { 'disabled': cancelDisabled }]"
          @click="$emit('cancel')" :disabled="cancelDisabled">
          {{ cancelLabel }}
        </button>

        <button class="button shared-modal-button" :class="[{ 'disabled': confirmDisabled }]" @click="$emit('confirm')"
          :disabled="confirmDisabled">
          {{ confirmLabel }}
        </button>
      </div>

      <transition name="shared-overlay-fade">
        <div v-if="overlayLoading" class="shared-filter-modal-overlay-loading">
          <div class="shared-list-spinner"></div>
          <span>{{ overlayLoadingLabel }}</span>
        </div>
      </transition>
    </div>
  </modal>
</template>

<script>
import GridComponent from '@/components/src/GridComponent'

export default {
  name: 'FilterActionModal',
  components: {
    'grid-component': GridComponent
  },
  props: {
    name: { type: String, required: true },
    title: { type: String, default: '' },
    height: { type: [String, Number], default: 'auto' },
    width: { type: [String, Number], default: '700px' },
    clickToClose: { type: Boolean, default: true },
    showFilter: { type: Boolean, default: true },
    filterLabel: { type: String, default: '' },
    filterInfoTitle: { type: String, default: '' },
    filterPlaceholder: { type: String, default: '' },
    filterValue: { type: String, default: '' },
    filterButtonLabel: { type: String, default: '' },
    filterButtonDisabled: { type: Boolean, default: false },
    cancelLabel: { type: String, default: '' },
    confirmLabel: { type: String, default: '' },
    cancelDisabled: { type: Boolean, default: false },
    confirmDisabled: { type: Boolean, default: false },
    showDefaultList: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    itemKeyField: { type: String, default: 'id' },
    itemCheckedField: { type: String, default: 'checked' },
    itemLabelBuilder: { type: Function, default: null },
    listLoading: { type: Boolean, default: false },
    listLoadingLabel: { type: String, default: '' },
    listEmptyLabel: { type: String, default: '' },
    overlayLoading: { type: Boolean, default: false },
    overlayLoadingLabel: { type: String, default: '' },
    selectedCount: { type: Number, default: null },
    grid: { type: Object, default: null },
    gridId: { type: String, default: '' },
    gridClass: { type: [String, Object, Array], default: '' }
  },
  computed: {
    showSelectedCount() {
      return Number.isFinite(this.selectedCount) && this.selectedCount >= 0;
    }
  },
  methods: {
    getItemLabel(item) {
      if (this.itemLabelBuilder) {
        return this.itemLabelBuilder(item);
      }
      return item && item.label ? item.label : '';
    }
  }
}
</script>

<style scoped lang="less">
.shared-filter-modal {
  width: 100%;
  max-width: 980px;
  min-height: 320px;
  max-height: 82vh;
  padding: 20px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  position: relative;
}

.shared-filter-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.shared-filter-modal-title {
  margin: 0;
}

.shared-filter-modal-counter {
  padding: .5em 1em;
  background-color: #212146 !important;
  font-size: .8em;
  color: white;
  border-radius: 16px;
}

.shared-filter-modal-filter {
  margin-bottom: 10px;
}

.shared-filter-modal-filter-label {
  margin: 0 0 10px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.shared-filter-modal-filter-inputs {
  display: flex;
  align-items: center;
  gap: 10px;
}

.shared-filter-modal-input {
  width: 100%;
  min-height: 36px;
  border: 1px solid #d7dee8;
  border-radius: 4px;
  padding: 0 8px;
  box-sizing: border-box;
}

.shared-filter-modal-content {
  min-height: 220px;
  overflow: auto;
}

.shared-filter-modal-grid.ag-grid-instance {
  height: 48vh;
  min-height: 320px;
}

.shared-filter-modal-content--framed {
  border: 1px solid #d7dee8;
  border-radius: 4px;
  background: #fff;
  padding: 6px;
  box-sizing: border-box;
}

.shared-list {
  border: 1px solid #d7dee8;
  border-radius: 4px;
  min-height: 320px;
  max-height: 54vh;
  overflow-y: auto;
  padding: 6px;
  background: #fff;
}

.shared-list-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 8px;
  border-bottom: 1px solid #e9eef5;
  border-radius: 4px;
  cursor: pointer;
  transition: background .15s ease, box-shadow .15s ease;
}

.shared-list-item:nth-child(odd) {
  background: #fbfcfe;
}

.shared-list-item:nth-child(even) {
  background: #f4f7fb;
}

.shared-list-item:hover {
  background: #eaf4ff;
  box-shadow: inset 0 0 0 1px #d3e7fb;
}

.shared-list-item-label {
  font-size: .95rem;
}

.shared-list-checkbox {
  appearance: none;
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  min-width: 18px;
  border: 2px solid #94a8bf;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  position: relative;
  transition: all .15s ease;
}

.shared-list-checkbox:hover {
  border-color: #2a8fdd;
  box-shadow: 0 0 0 2px rgba(42, 143, 221, .15);
}

.shared-list-checkbox:checked {
  background: #2a8fdd;
  border-color: #2a8fdd;
}

.shared-list-checkbox:checked::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 0;
  width: 5px;
  height: 10px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.shared-list-checkbox:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(42, 143, 221, .2);
}

.shared-list-empty,
.shared-list-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 300px;
  font-size: .95rem;
  color: #4b4b4b;
}

.shared-list-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(0, 0, 0, .15);
  border-top-color: #2a8fdd;
  border-radius: 50%;
  animation: shared-spin .9s linear infinite;
}

.shared-filter-modal-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.shared-filter-modal-overlay-loading {
  position: absolute;
  inset: 0;
  z-index: 4;
  background: rgba(255, 255, 255, .82);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 6px;
}

.shared-overlay-fade-enter-active,
.shared-overlay-fade-leave-active {
  transition: opacity .2s ease;
}

.shared-overlay-fade-enter,
.shared-overlay-fade-leave-to {
  opacity: 0;
}

.shared-modal-button {
  min-width: 120px;
}

@media (max-width: 700px) {
  .shared-filter-modal {
    width: 100%;
    padding: 14px;
    max-height: 88vh;
  }

  .shared-filter-modal-filter-inputs {
    flex-direction: column;
    align-items: stretch;
  }

  .shared-modal-button {
    width: 100%;
  }

  .shared-filter-modal-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .shared-list {
    min-height: 260px;
    max-height: 45vh;
  }
}

@keyframes shared-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
